import { type ReactNode, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronGrabberVertical, ChevronLeftOutline } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { FilterPopover } from "@acto/taskpane/field-sales-2/components/patterns/FilterPopover";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import { TitleText } from "@acto/taskpane/field-sales-2/components/primitives/TitlePill";
import { glassCapsulePress } from "@acto/taskpane/field-sales-2/styles/glassCapsulePress";

export interface DrillLevel {
  id: string;
  name: string;
}

// The row spans the card at every level: only the control that fills it changes between them.
const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
}));

const tintedGlass = (tint: string, base: string) => `linear-gradient(0deg, ${tint}, ${tint}), ${base}`;

const DrillControl = styled(Pill)(({ theme }) => ({
  gap: theme.fieldSalesTwo.spacing[12],
  height: theme.fieldSalesTwo.spacing[50],
  margin: 0,
  cursor: "pointer",
  background: tintedGlass(theme.fieldSalesTwo.colors.neutral[200], theme.fieldSalesTwo.colors.surface.glassStrong),
  color: theme.fieldSalesTwo.colors.neutral[900],
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  ...glassCapsulePress(theme, { restShadow: theme.fieldSalesTwo.shadows.glass }),
}));

const PathTrigger = styled(DrillControl)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  // The grabber is drawn on its own 22 grid, so it carries its own size.
  "& svg": {
    flexShrink: 0,
    color: theme.fieldSalesTwo.colors.neutral[900],
  },
}));

// A flex child keeps min-width: auto, so without this the ellipsis never engages.
const PathLabel = styled(TitleText)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
}));

// Stepping back is the quieter move, so its two controls carry softer ink than the path pill.
const BackCircle = styled(DrillControl)(({ theme }) => ({
  width: theme.fieldSalesTwo.spacing[50],
  justifyContent: "center",
  flex: "0 0 auto",
  padding: 0,
  color: theme.fieldSalesTwo.colors.neutral[850],
  // The chevron is a tall, narrow glyph, so it is sized by height; a square box would fit it to
  // the width and blow the glyph up to the box's height.
  "& svg": {
    height: theme.fieldSalesTwo.spacing[18],
    width: "auto",
  },
}));

export const DrillBackButton = styled(DrillControl)(({ theme }) => ({
  flex: 1,
  justifyContent: "center",
  color: theme.fieldSalesTwo.colors.neutral[850],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
}));

interface TalkingPointDrillNavProps {
  stack: Pick<DrillLevel, "name">[];
  onDrillTo: (depth: number) => void;
}

function PathSelect({ stack, onDrillTo }: TalkingPointDrillNavProps) {
  const t = useTranslations("fieldSalesTwo.drill");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const currentLevel = stack.at(-1);
  if (!currentLevel) return null;

  // Item keys are the drill depth to keep when the entry is picked; the leading entry is depth 0.
  const pathGroups = [
    { key: "root", items: [{ key: "0", label: t("guide") }] },
    { key: "path", items: stack.map((level, index) => ({ key: String(index + 1), label: level.name })) },
  ];

  return (
    <>
      <PathTrigger
        as="button"
        variant="action"
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
      >
        <PathLabel>{currentLevel.name}</PathLabel>
        <ChevronGrabberVertical />
      </PathTrigger>
      <FilterPopover
        anchorRef={triggerRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        align="start"
        groups={pathGroups}
        selectedKeys={[String(stack.length)]}
        onToggle={(key) => {
          onDrillTo(Number(key));
          setOpen(false);
        }}
        ariaLabel={t("levelSwitcher")}
      />
    </>
  );
}

export function TalkingPointDrillNav({ stack, onDrillTo }: TalkingPointDrillNavProps): ReactNode {
  const t = useTranslations("fieldSalesTwo.drill");
  const tNav = useTranslations("fieldSalesTwo.customerNav");

  if (stack.length < 2)
    return (
      <Row>
        <DrillBackButton as="button" variant="action" type="button" onClick={() => onDrillTo(0)}>
          {t("backToTalkingPoint")}
        </DrillBackButton>
      </Row>
    );

  return (
    <Row>
      <BackCircle
        as="button"
        variant="action"
        type="button"
        onClick={() => onDrillTo(stack.length - 1)}
        aria-label={tNav("back")}
      >
        <ChevronLeftOutline aria-hidden="true" />
      </BackCircle>
      <PathSelect stack={stack} onDrillTo={onDrillTo} />
    </Row>
  );
}
